import { capitalize } from '@ember/string';
import Component from '@glimmer/component';
import type Entry from 'garaje/models/entry';
import type Invite from 'garaje/models/invite';
import sortByPosition from 'garaje/utils/sort-by-position';
import _uniqBy from 'lodash/uniqBy';

interface QuestionnairesActivitySignature {
  Args: {
    entry?: Entry;
    invite?: Invite;
  };
}

interface DisplayQuestionnaireStatus {
  id: string;
  title: string;
  status: string;
}

export default class QuestionnairesActivity extends Component<QuestionnairesActivitySignature> {
  get questionnairesWithStatus(): DisplayQuestionnaireStatus[] {
    const model = this.args.invite ?? this.args.entry;
    if (!model) return [];

    if (model.questionnaireResponseSummary) {
      // If we have a `questionnaireResponseSummary`, use its data to build the list of questionnaires & status.
      return model.questionnaireResponseSummary.requiredQuestionnaires.toSorted(sortByPosition).map((questionnaire) => {
        const data = {
          id: questionnaire.id,
          title: questionnaire.title,
          status: 'Incomplete', // default to "Incomplete"; this will get overridden further down if we receive an actual status
        };

        const completionSummary = model.questionnaireResponseSummary?.completionSummary.find(
          (completionSummary) => completionSummary.questionnaireId === questionnaire.id,
        );

        if (completionSummary) {
          data['status'] = capitalize(completionSummary.status);
        }
        return data;
      });
    } else {
      // If we didn't get a `questionnaireResponseSummary`, use the questionnaires associated with the flow
      // to build the list, and use "Incomplete" as the status for all.
      const flowQuestionnaires = model.flow?.content?.flowQuestionnaires.slice().toSorted(sortByPosition);
      const questionnaires = (flowQuestionnaires ?? []).map((fq) => fq.questionnaire);

      return questionnaires.map((questionnaire) => ({
        id: questionnaire.id,
        title: questionnaire.title,
        status: 'Incomplete',
      }));
    }
  }

  <template>
    {{#if this.questionnairesWithStatus}}
      <div class="border-b pb-4 mb-4" data-test-questionnaires-status-section>
        <h4 class="text-sm capitalize">Assessments</h4>
        <ul class="list-none p-0 m-0 leading-normal mt-2">
          {{#each this.questionnairesWithStatus as |record|}}
            <li
              class="mb-1 ml-6 text-sm text-carbon-70 list-inside"
              style="text-indent: -1.5rem"
              data-test-questionnaires-status={{record.id}}
            >
              {{record.title}}
              <span class="text-carbon-50">
                -
                {{record.status}}
              </span>
            </li>
          {{/each}}
        </ul>
      </div>
    {{/if}}
  </template>
}
